import React, { useContext, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { CartContext } from "../../context/CartContext";
import APP_BASE_URL, { IMG_BASE_URL } from "../../components/Api/globalApi";
import { FaTrash, FaArrowLeft, FaMinus, FaPlus } from "react-icons/fa";
import { virtualMallProfileApi, virtualMallCheckoutApi, virtualMallIncentiveListApi } from "./VirtualMallApi/api";
import Swal from "sweetalert2";
import "./Cart.css";

const Cart = () => {
  const {
    cartItems,
    removeFromCart,
    incrementQuantity,
    decrementQuantity,
    cartTotalCount,
    fetchCart,
  } = useContext(CartContext);
  const navigate = useNavigate();
  const imageBaseUrl = IMG_BASE_URL;
  const [userProfile, setUserProfile] = useState(null);
  const [purchasePointsBalance, setPurchasePointsBalance] = useState(0);

  const getItemPoints = (item) => Number(item?.vsp_point ?? item?.points ?? 0);
  const totalPoints = cartItems.reduce((acc, item) => acc + getItemPoints(item) * item.quantity, 0);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [profileRes, incentiveRes] = await Promise.all([
          virtualMallProfileApi(),
          virtualMallIncentiveListApi()
        ]);

        if (profileRes?.status) {
          setUserProfile(profileRes.data);
        }

        if (incentiveRes?.status || incentiveRes?.result) {
          setPurchasePointsBalance(parseFloat(incentiveRes.purchasePoints) || 0);
        }
      } catch (err) {
        console.error("Failed to fetch cart checkout data", err);
      }
    };

    fetchData();
  }, []);

  const handleCheckout = async () => {
    if (cartItems.length === 0) return;

    const availablePoints = purchasePointsBalance;

    if (totalPoints > availablePoints) {
      Swal.fire({
        icon: "error",
        title: "Insufficient Points",
        text: `You need ${totalPoints} points, but your Purchase Point balance is ${availablePoints} points.`,
        confirmButtonColor: "#3b82f6",
      });
      return;
    }

    const { value: formValues } = await Swal.fire({
      title: "Confirm Checkout",
      html: `
        <div class="checkout-swal-form">
          <div class="swal-field-group">
            <label>Delivery Address</label>
            <textarea id="swal-address" class="swal2-textarea" placeholder="Enter full address">${userProfile?.vu_address || ""}</textarea>
          </div>
          <div class="swal-field-row">
            <div class="swal-field-group">
              <label>District</label>
              <input id="swal-district" class="swal2-input" placeholder="District" value="${userProfile?.vu_district || ""}">
            </div>
            <div class="swal-field-group">
              <label>State</label>
              <input id="swal-state" class="swal2-input" placeholder="State" value="${userProfile?.vu_state || ""}">
            </div>
          </div>
          <div class="swal-field-row">
            <div class="swal-field-group">
              <label>Pincode</label>
              <input id="swal-pincode" class="swal2-input" placeholder="6-digit Pincode" maxlength="6" value="${userProfile?.vu_pincode || ""}">
            </div>
            <div class="swal-field-group">
              <label>Transaction Password</label>
              <input id="swal-password" type="password" class="swal2-input" placeholder="Password">
            </div>
          </div>
          <div class="swal-field-row">
            <div class="swal-field-group" style="grid-column: span 2;">
              <label>Shop Purchase List</label>
              <input id="swal-shop-purchase" class="swal2-input" placeholder="Enter Shop Purchase details (Optional)">
            </div>
          </div>
          <div class="swal-summary-info">
             <p>Total Points: <strong>${totalPoints} pts</strong></p>
             <p>Purchase Point: <strong>${purchasePointsBalance} pts</strong></p>
          </div>
        </div>
      `,
      focusConfirm: false,
      showCancelButton: true,
      confirmButtonText: "Place Order",
      confirmButtonColor: "#10b981",
      cancelButtonColor: "#ef4444",
      preConfirm: () => {
        const address = document.getElementById("swal-address").value;
        const district = document.getElementById("swal-district").value;
        const state = document.getElementById("swal-state").value;
        const pincode = document.getElementById("swal-pincode").value;
        const password = document.getElementById("swal-password").value;
        const shopPurchase = document.getElementById("swal-shop-purchase").value;

        if (!address || !district || !state || !pincode || !password) {
          Swal.showValidationMessage("Please fill in all fields");
          return false;
        }

        if (!/^\d{6}$/.test(pincode)) {
          Swal.showValidationMessage("Please enter a valid 6-digit pincode");
          return false;
        }

        return { address, district, state, pincode, password, shopPurchase };
      },
    });

    if (formValues) {
      try {
        Swal.fire({
          title: "Processing...",
          didOpen: () => Swal.showLoading(),
          allowOutsideClick: false,
        });

        const res = await virtualMallCheckoutApi({
          address: formValues.address,
          district: formValues.district,
          state: formValues.state,
          pincode: formValues.pincode,
          transaction_password: formValues.password,
          shop_purchase: formValues.shopPurchase,
          totalPoints,
          items: cartItems,
        });

        if (res.status) {
          const uniqueShopIds = [...new Set(cartItems.map((item) => item?.vsp_shop_id).filter(Boolean))];
          const nextRoute =
            uniqueShopIds.length === 1
              ? `/shop-products?shop_id=${uniqueShopIds[0]}`
              : "/virtual-mall";

          await Swal.fire({
            icon: "success",
            title: "Order Placed!",
            text: "Your order request has been submitted successfully. Purchase Points have been deducted. Activity Points will be credited after admin approval.",
            confirmButtonColor: "#10b981",
          });
          fetchCart();
          navigate(nextRoute);
        } else {
          Swal.fire({
            icon: "error",
            title: "Checkout Failed",
            text: res.message || "Something went wrong",
            confirmButtonColor: "#3b82f6",
          });
        }
      } catch (err) {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: err.response?.data?.message || "Internal server error",
          confirmButtonColor: "#3b82f6",
        });
      }
    }
  };

  const goBack = () => navigate(-1);

  return (
    <div className="cart-page">
      <div className="cart-container">
        <header className="cart-header">
          <button onClick={goBack} className="cart-back-btn">
            <FaArrowLeft /> Back
          </button>
          <div className="cart-header-title">
            <h1>Shopping Cart</h1>
            <span className="cart-count-badge">{cartTotalCount} Items</span>
          </div>
        </header>

        {cartItems.length === 0 ? (
          <div className="cart-empty-state">
            <div className="cart-empty-icon">Cart</div>
            <h2>Your cart is empty</h2>
            <p>Looks like you haven&apos;t added anything to your cart yet.</p>
            <button className="cart-continue-btn" onClick={() => navigate("/virtual-mall")}>
              Continue Shopping
            </button>
          </div>
        ) : (
          <div className="cart-content">
            <div className="cart-items-list">
              {cartItems.map((item) => {
                const itemPoints = getItemPoints(item);
                const itemTotalPoints = itemPoints * item.quantity;

                return (
                  <div key={item.vsp_id} className="cart-item-card">
                    <div className="cart-item-image-wrap">
                      {item.vsp_image ? (
                        <img
                          src={`${imageBaseUrl}/${item.vsp_image}`}
                          alt={item.vsp_name}
                          className="cart-item-image"
                          onError={(e) => {
                            e.target.style.display = "none";
                            e.target.nextSibling.style.display = "flex";
                          }}
                        />
                      ) : null}
                      <div className="cart-item-fallback" style={{ display: item.vsp_image ? "none" : "flex" }}>
                        No Image
                      </div>
                    </div>

                    <div className="cart-item-details">
                      <div className="cart-item-top">
                        <div className="cart-item-category-wrap">
                          <span className="cart-item-category">Category {item.vsp_category}</span>
                          {itemPoints > 0 && (
                            <span className="cart-item-points-badge">{itemPoints} pts each</span>
                          )}
                        </div>
                        <button
                          className="cart-item-remove"
                          onClick={() => removeFromCart(item.vsp_id)}
                          title="Remove item"
                        >
                          <FaTrash />
                        </button>
                      </div>
                      <h3 className="cart-item-title">{item.vsp_name}</h3>
                      <p className="cart-item-desc">{item.vsp_description}</p>
                      {itemPoints > 0 && (
                        <div className="cart-item-points-total">
                          Item total: <strong>{itemTotalPoints} pts</strong>
                        </div>
                      )}
                      <div className="cart-item-qty-controls">
                        <button
                          type="button"
                          className="cart-qty-btn"
                          onClick={() => decrementQuantity(item.vsp_id)}
                          disabled={item.quantity <= 1}
                          aria-label={`Decrease quantity for ${item.vsp_name}`}
                        >
                          <FaMinus />
                        </button>
                        <span className="cart-item-qty">
                          Qty: <strong>{item.quantity}</strong>
                        </span>
                        <button
                          type="button"
                          className="cart-qty-btn"
                          onClick={() => incrementQuantity(item.vsp_id)}
                          aria-label={`Increase quantity for ${item.vsp_name}`}
                        >
                          <FaPlus />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="cart-summary">
              <h2>Order Summary</h2>
              <div className="cart-summary-row">
                <span>Subtotal ({cartTotalCount} items)</span>
                <span>To be calculated</span>
              </div>
              <div className="cart-summary-row points-row">
                <span>Total Points</span>
                <strong className="text-warning">{totalPoints} pts</strong>
              </div>

              <div className="cart-summary-divider"></div>
              <div className="cart-summary-total">
                <span>Total</span>
                <span>Pending Price</span>
              </div>

              <button className="cart-checkout-btn" onClick={handleCheckout}>
                Proceed to Checkout
              </button>

              <div className="cart-secure-badge">Secure Checkout</div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Cart;
